import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Landmark } from 'lucide-react';
import { PROPERTY_KIND_LABELS, PROPERTY_USAGE_LABELS } from '@/features/properties/labels';
import { useArchiveProperty, usePropertyCosts, useProperty, useSavePropertyCosts } from '@/features/properties/queries';
import { PropertyCostsForm } from '@/features/properties/PropertyCostsForm';
import { useLoans } from '@/features/loans/queries';
import { formatCents, formatIsoDate, formatRatio } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { IconActionButton } from '@/components/ui/icon-action-button';
import { Separator } from '@/components/ui/separator';

export function PropertyDetailPage() {
  const { propertyId } = useParams({ strict: false }) as { propertyId: string };
  const id = Number(propertyId);

  const [includeArchivedLoans, setIncludeArchivedLoans] = useState(false);
  const property = useProperty(id);
  const costs = usePropertyCosts(id);
  const loans = useLoans(id, includeArchivedLoans);
  const archiveProperty = useArchiveProperty(id);
  const saveCosts = useSavePropertyCosts(id);

  if (property.isPending) return <p className="text-muted-foreground">Chargement…</p>;
  if (property.isError || !property.data) return <p className="text-destructive">Bien introuvable.</p>;

  const data = property.data;
  const archived = data.archivedAt !== null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <IconActionButton label="Retour aux biens" variant="outline" asChild>
            <Link to="/biens">
              <ArrowLeft />
            </Link>
          </IconActionButton>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold">
              {data.name}
              {archived && <Badge variant="secondary">Archivé</Badge>}
            </h1>
            <p className="text-muted-foreground">
              {PROPERTY_KIND_LABELS[data.kind]} · {PROPERTY_USAGE_LABELS[data.usage]}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/biens/$propertyId/modifier" params={{ propertyId }}>
              Modifier
            </Link>
          </Button>
          <Button
            variant="outline"
            disabled={archiveProperty.isPending}
            onClick={() =>
              archiveProperty.mutate(!archived, {
                onSuccess: () => toast.success(archived ? 'Bien rétabli' : 'Bien archivé'),
                onError: (error) => toast.error(apiErrorMessage(error, "Échec de l'archivage")),
              })
            }
          >
            {archived ? 'Rétablir' : 'Archiver'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informations</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <Info label="Adresse" value={[data.address, data.postalCode, data.city].filter(Boolean).join(', ') || '—'} />
          <Info label="Pays" value={data.country} />
          <Info label="Surface" value={data.surfaceM2 ? `${data.surfaceM2} m²` : '—'} />
          <Info label="Pièces" value={data.rooms ?? '—'} />
          <Info label="Date d'acquisition" value={formatIsoDate(data.acquiredOn)} />
          <Info label="Devise" value={data.currency} />
          {data.notes && <Info label="Notes" value={data.notes} className="sm:col-span-2" />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Coûts d'acquisition</CardTitle>
        </CardHeader>
        <CardContent>
          {costs.isPending ? (
            <p className="text-muted-foreground">Chargement…</p>
          ) : costs.isError ? (
            <p className="text-destructive">
              Impossible de charger les coûts d'acquisition. Rechargez la page avant de les modifier.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {costs.data && (
                <p className="text-sm text-muted-foreground">
                  Coût total du projet : <span className="font-medium text-foreground">{formatCents(costs.data.totalCents, data.currency)}</span>
                </p>
              )}
              <PropertyCostsForm
                costs={costs.data}
                isPending={saveCosts.isPending}
                onSubmit={(input) =>
                  saveCosts.mutate(input, {
                    onSuccess: () => toast.success('Coûts enregistrés'),
                    onError: (error) =>
                      toast.error(apiErrorMessage(error, 'Échec de l’enregistrement')),
                  })
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Prêts</h2>
        <Button asChild variant="outline">
          <Link to="/biens/$propertyId/prets/nouveau" params={{ propertyId }}>
            <Plus /> Nouveau prêt
          </Link>
        </Button>
      </div>

      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={includeArchivedLoans}
          onChange={(event) => setIncludeArchivedLoans(event.target.checked)}
          className="size-4 rounded border-input"
        />
        Afficher les prêts archivés
      </label>

      {loans.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {loans.isError && <p className="text-destructive">Impossible de charger les prêts.</p>}
      {loans.data?.length === 0 && <p className="text-muted-foreground">Aucun prêt pour ce bien.</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {loans.data?.map((loan) => (
          <Link key={loan.id} to="/biens/$propertyId/prets/$loanId" params={{ propertyId, loanId: String(loan.id) }}>
            <Card className="h-full transition-colors hover:border-primary/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Landmark className="size-4" /> {loan.lender}
                  {loan.archivedAt && <Badge variant="secondary">Archivé</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-1 text-sm text-muted-foreground">
                <p>{formatCents(loan.principalCents, data.currency)} · {formatRatio(loan.annualRate)}</p>
                <p>{loan.hasSchedule ? 'Échéancier généré' : 'Échéancier non généré'}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Info({ label, value, className }: { label: string; value: string | number; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
